import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';

export interface BatchingSettings {
  anchor: 'MONTHDAY' | 'WEEKDAY' | 'YEARDAY';
  anchorWeek: { values: number[] };
  anchorMonth: { values: number[] };
  anchorYear: { values: { month: number; day: number }[] };
  minimumDaysBetweenOrders: number;
}

export const isDateValidByBatching = ({
  date,
  batchingSettings,
}: {
  date: Date;
  batchingSettings: BatchingSettings;
}): boolean => {
  if (batchingSettings?.anchor === 'MONTHDAY') {
    const day = dayjs(date).date();

    return batchingSettings?.anchorMonth?.values.includes(day);
  }

  if (batchingSettings?.anchor === 'WEEKDAY') {
    const day = dayjs(date).day();
    // We need to convert the day as dayjs library is taking Sunday as day 0
    // of the week, which is our day 7. The rest of the days are matching ok
    const convertedDay = day === 0 ? 7 : day;

    return batchingSettings?.anchorWeek?.values.includes(convertedDay);
  }

  if (batchingSettings?.anchor === 'YEARDAY') {
    const month = dayjs(date).month() + 1;
    const day = dayjs(date).date();

    return !!batchingSettings.anchorYear.values.find(
      (v) => v.month === month && v.day === day,
    );
  }

  return true;
};

export const getNextAvailableDateByBatching = ({
  batchingSettings,
}: {
  batchingSettings: BatchingSettings;
}): dayjs.Dayjs => {
  if (
    !batchingSettings ||
    isEmpty(batchingSettings) ||
    !batchingSettings.anchor
  ) {
    return dayjs().add(1, 'day');
  }
  const now = dayjs();
  let nextAvailableDate = now.clone().add(1, 'day');
  if (batchingSettings.anchor === 'MONTHDAY') {
    nextAvailableDate = getNextAvailableDateByMonthDay({
      batchingSettings,
      nextAvailableDate,
    });
  } else if (batchingSettings.anchor === 'WEEKDAY') {
    nextAvailableDate = getNextAvailableDateByWeekDay({
      batchingSettings,
      nextAvailableDate,
    });
  } else if (batchingSettings.anchor === 'YEARDAY') {
    nextAvailableDate = getNextAvailableDateByYearDay({
      batchingSettings,
      nextAvailableDate,
    });
  }
  return nextAvailableDate;
};

export const getNextAvailableDateByMonthDay = ({
  batchingSettings,
  nextAvailableDate,
}: {
  batchingSettings: BatchingSettings;
  nextAvailableDate: dayjs.Dayjs;
}): dayjs.Dayjs => {
  const day = nextAvailableDate.date();
  if (batchingSettings?.anchorMonth?.values.includes(day)) {
    return nextAvailableDate;
  }
  const nextAvailableDateByBatching = nextAvailableDate.clone().add(1, 'day');
  return getNextAvailableDateByMonthDay({
    batchingSettings,
    nextAvailableDate: nextAvailableDateByBatching,
  });
};

export const getNextAvailableDateByWeekDay = ({
  batchingSettings,
  nextAvailableDate,
}: {
  batchingSettings: BatchingSettings;
  nextAvailableDate: dayjs.Dayjs;
}): dayjs.Dayjs => {
  const day = nextAvailableDate.day();
  const convertedDay = day === 0 ? 7 : day;
  if (batchingSettings?.anchorWeek?.values.includes(convertedDay)) {
    return nextAvailableDate;
  }
  const nextAvailableDateByBatching = nextAvailableDate.clone().add(1, 'day');
  return getNextAvailableDateByWeekDay({
    batchingSettings,
    nextAvailableDate: nextAvailableDateByBatching,
  });
};

const getNextAvailableDateByYearDay = ({
  batchingSettings,
  nextAvailableDate,
}: {
  batchingSettings: BatchingSettings;
  nextAvailableDate: dayjs.Dayjs;
}): dayjs.Dayjs => {
  for (const { month, day } of batchingSettings.anchorYear.values) {
    const updated = nextAvailableDate.set('date', day).set('month', month - 1);
    if (updated >= nextAvailableDate) {
      return updated;
    }
  }

  // if next available date is more than 3 years from now, bail. This should never happen
  if (nextAvailableDate.year() - 3 > dayjs().year()) {
    return nextAvailableDate;
  }

  return getNextAvailableDateByYearDay({
    batchingSettings,
    nextAvailableDate: nextAvailableDate.add(1, 'year'),
  });
};
